import React from "react";
import {
  Box,
  Button,
  Paper,
  Grid,
  alpha,
  useTheme,
  Typography,
} from "@mui/material";
import {
  Print,
  Share,
  CheckCircle,
  Cancel,
  Schedule,
  Done,
  TaskAlt,
} from "@mui/icons-material";

const OrderActionsSection = ({
  orderDetails,
  onStatusUpdate,
  onPrint,
  onShare,
}) => {
  const theme = useTheme();

  // All possible forward statuses in order ("served" excluded for takeaway)
  const isTakeaway = orderDetails.orderType === "takeaway";
  const STATUS_FLOW = [
    {
      status: "preparing",
      label: "Mark as Preparing",
      icon: Schedule,
      color: { main: "#667eea", dark: "#764ba2" },
    },
    {
      status: "ready",
      label: "Mark as Ready",
      icon: CheckCircle,
      color: { main: "#10b981", dark: "#059669" },
    },
    ...(!isTakeaway
      ? [
        {
          status: "served",
          label: "Mark as Served",
          icon: Done,
          color: { main: "#6366f1", dark: "#4f46e5" },
        },
      ]
      : []),
    {
      status: "completed",
      label: "Mark as Completed",
      icon: TaskAlt,
      color: { main: "#059669", dark: "#047857" },
    },
  ];

  const currentStatus = orderDetails.status;
  const isFinal =
    currentStatus === "completed" || currentStatus === "cancelled";
  const isPaid =
    orderDetails.paymentStatus === "completed" ||
    orderDetails.paymentStatus === "paid";

  // Determine the index of the current status so we only show future statuses
  const currentFlowIndex = STATUS_FLOW.findIndex(
    (s) => s.status === currentStatus,
  );
  // For pending/confirmed, treat as "before preparing" (index -1 means show all)
  const startFromIndex = currentFlowIndex === -1 ? 0 : currentFlowIndex + 1;

  const statusButtons = [
    // Show all future statuses — staff can jump to any one they choose
    ...STATUS_FLOW.slice(startFromIndex),
    // Cancel is available unless order is already terminal or already paid
    ...(!isFinal && !isPaid
      ? [
        {
          label: "Cancel Order",
          icon: Cancel,
          color: { main: "#ef4444", dark: "#dc2626" },
          status: "cancelled",
        },
      ]
      : []),
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: "white",
        borderRadius: "16px",
        p: 2.5,
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        boxShadow: `0 2px 8px ${alpha("#000", 0.04)}`,
        height: "100%",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: theme.palette.text.primary,
          mb: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "12px",
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
          }}
        >
          <CheckCircle sx={{ fontSize: 24, color: "white" }} />
        </Box>
        Order Status Actions
      </Typography>

      <Grid container spacing={2}>
        {statusButtons.map((button, index) => {
          const isDisabled = button.status === "completed" && !isPaid;
          return (
            <Grid size={{ xs: 4 }} key={index}>
              <Box sx={{ position: "relative" }}>
                <Button
                  variant="contained"
                  onClick={() => !isDisabled && onStatusUpdate(button.status)}
                  disabled={isDisabled}
                  title={
                    isDisabled
                      ? "Add payment before completing the order"
                      : undefined
                  }
                  sx={{
                    width: "100%",
                    aspectRatio: "1 / 1",
                    borderRadius: "16px",
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    textTransform: "none",
                    letterSpacing: "0.2px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    position: "relative",
                    overflow: "hidden",
                    minWidth: 0,
                    p: 1,
                    background: isDisabled
                      ? undefined
                      : `linear-gradient(135deg, ${button.color.main} 0%, ${button.color.dark} 100%)`,
                    bgcolor: isDisabled
                      ? alpha(theme.palette.divider, 0.15)
                      : undefined,
                    color: isDisabled ? theme.palette.text.disabled : "white",
                    boxShadow: isDisabled
                      ? "none"
                      : `0 4px 14px ${alpha(button.color.main, 0.4)}`,
                    border: isDisabled
                      ? `2px dashed ${alpha(theme.palette.divider, 0.4)}`
                      : "none",
                    "&::before": isDisabled
                      ? {}
                      : {
                        content: '""',
                        position: "absolute",
                        top: -30,
                        right: -30,
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        background: alpha("#fff", 0.15),
                      },
                    "&:hover": isDisabled
                      ? {}
                      : {
                        background: `linear-gradient(135deg, ${button.color.dark} 0%, ${button.color.main} 100%)`,
                        transform: "translateY(-3px)",
                        boxShadow: `0 10px 28px ${alpha(button.color.main, 0.45)}`,
                      },
                    transition: "all 0.3s ease",
                  }}
                >
                  <button.icon
                    sx={{ fontSize: 28, position: "relative", zIndex: 1 }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 800,
                      position: "relative",
                      zIndex: 1,
                      lineHeight: 1.2,
                      textAlign: "center",
                    }}
                  >
                    {button.label}
                  </Typography>
                  {isDisabled && (
                    <Typography
                      sx={{
                        fontSize: "0.6rem",
                        fontWeight: 600,
                        position: "relative",
                        zIndex: 1,
                        color: "#ef4444",
                        lineHeight: 1.1,
                        textAlign: "center",
                      }}
                    >
                      Payment required
                    </Typography>
                  )}
                </Button>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
};

export default OrderActionsSection;
